import React, { useState } from 'react';
import clsx from 'clsx';
import { ExpiredDocumentsIcon, InProgressIcon, SignedDocumentsIcon } from './Icons';
import { SigningProcess, SigningProcessesStatusCountsResponse } from '../types';
import { AnimatedNumber } from '../../common/AnimatedNumber';
import { Input } from '../../common/ui/Input';

export type StatusFilter = SigningProcess['status'] | `${SigningProcess['status']},${SigningProcess['status']}`;

interface FilterConfig {
  key: StatusFilter;
  label: string;
  icon: React.ReactNode;
  activeClassName: string;
  inactiveClassName: string;
}

const FILTERS_CONFIG: FilterConfig[] = [
  {
    key: 'PROGRESS',
    label: 'En proceso de firma',
    activeClassName: 'bg-warning text-white border-warning',
    inactiveClassName: 'bg-white text-warning border-warning',
    icon: <InProgressIcon />,
  },
  {
    key: 'LAPSED,FAILED',
    label: 'Links de firma caducados ',
    activeClassName: 'bg-danger text-white border-danger',
    inactiveClassName: 'bg-white text-danger border-danger',
    icon: <ExpiredDocumentsIcon />,
  },
  {
    key: 'SIGNED',
    label: 'Firmas exitosas',
    activeClassName: 'bg-success text-white border-success',
    inactiveClassName: 'bg-white text-success border-success',
    icon: <SignedDocumentsIcon />,
  },
];
interface StatusFilterButtonProps {
  label: string;
  isActive: boolean;
  icon: React.ReactNode;
  activeClassName: string;
  inactiveClassName: string;
  onClick: () => void;
  count: number;
  isLoading: boolean;
}

export function StatusFilterButton({
  label,
  isActive,
  icon,
  activeClassName,
  inactiveClassName,
  onClick,
  count,
  isLoading,
}: StatusFilterButtonProps) {
  return (
    <button
      type="button"
      data-bs-toggle="button"
      className={clsx('btn rounded-4 px-6 py-4 border fw-bold w-100',
        isActive ? activeClassName : inactiveClassName,
      )}
      aria-pressed={isActive}
      onClick={onClick}
    >
      {icon}
      <div className="d-flex flex-column align-items-center">
        {isLoading ? (
          <div className="placeholder col-2 rounded-1" />
        ) : (
          <AnimatedNumber end={count} />
        )}
        {label}
      </div>
    </button>
  );
}

export function StatusFilters(
  {
    onChange, statusCounts, isLoading, initialFilters,
  }:
  {
    onChange?: (active: StatusFilter[]) => void;
    statusCounts?: SigningProcessesStatusCountsResponse;
    isLoading: boolean;
    initialFilters: StatusFilter[];
  },
) {
  const [active, setActive] = useState<StatusFilter[]>(initialFilters);

  const toggle = (key: StatusFilter) => {
    setActive((prev) => {
      // If the clicked filter is already active, deactivate it (empty array)
      // Otherwise, set only this filter as active (single item array)
      const next = prev.includes(key) ? [] : [key];
      onChange?.(next);
      return next;
    });
  };

  const getStatusCount = (filterKey: StatusFilter): number => {
    if (!statusCounts) return 0;

    // If it's a compound filter (contains comma), sum the individual counts
    if (filterKey.includes(',')) {
      const statuses = filterKey.split(',') as SigningProcess['status'][];
      return statuses.reduce((sum, status) => sum + (statusCounts[status] ?? 0), 0);
    }

    // Single status filter
    return statusCounts[filterKey as SigningProcess['status']] ?? 0;
  };

  return (
    <div className="d-inline-flex flex-column flex-md-row gap-2 w-100">
      {FILTERS_CONFIG.map((filter) => {
        const isActive = active.includes(filter.key);
        return (
          <StatusFilterButton
            key={filter.key}
            label={filter.label}
            count={getStatusCount(filter.key)}
            isLoading={isLoading}
            isActive={isActive}
            icon={filter.icon}
            activeClassName={filter.activeClassName}
            inactiveClassName={filter.inactiveClassName}
            onClick={() => toggle(filter.key)}
          />
        );
      })}
    </div>
  );
}

export function SearchInput({
  value, onChange,
}: { value: string, onChange: React.InputHTMLAttributes<HTMLInputElement>['onChange'] }) {
  return (
    <div className="position-relative w-100">
      <Input
        type="text"
        id="search-rut"
        placeholder="Buscar RUT cliente"
        value={value}
        onChange={onChange}
        aria-label="Buscar RUT cliente"
        autoComplete="off"
      />
      <button
        type="button"
        className="btn position-absolute top-50 end-0 translate-middle-y p-4 border-0"
        aria-label="Buscar por RUT"
        tabIndex={0}

      >
        <img src="/static/img/icons/search.svg" width="24" height="24" alt="" aria-hidden="true" />
      </button>
    </div>
  );
}
